// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Controller. Owns all state, the crypto and the transport session, and drives
 * the pure views in ui.ts. The secret is held only in a module-local variable
 * that is nulled the instant it is delivered (sender) or burned (receiver) — it
 * is never written to storage, the URL, or the event log.
 */
import './styles/main.css';
import { deriveSecretKey, deriveSasWords, encryptBytes, decryptBytes } from './crypto.ts';
import { generateRoomId, generatePassphrase, normalizePass } from './room.ts';
import { readInvite, currentInviteUrl } from './router.ts';
import { encodeSecret, decodeSecret, packFrame, unpackFrame, describeSecret } from './secret.ts';
import {
  senderOnRequest,
  senderOnAck,
  senderOnCancel,
  receiverOnPeer,
  receiverOnReveal,
  receiverOnSecret,
  receiverOnGone,
  receiverOnBurn,
} from './handoff.ts';
import { BurnSession } from './session.ts';
import { emit, mountEventDrawer } from './eventlog.ts';
import { initModals, openModal, isModalOpen, toast, howItWorksHtml, privacyHtml, aboutHtml } from './modals.ts';
import { initGlossary } from './glossary.ts';
import * as ui from './ui.ts';
import { fmtClock } from './format.ts';
import type { SenderState, ReceiverState } from './types.ts';

// ── Module state ─────────────────────────────────────────
let refs: ui.ShellRefs;
let session: BurnSession | null = null;

let role: 'sender' | 'receiver' = 'sender';
let senderState: SenderState = 'composing';
let receiverState: ReceiverState = 'connecting';

let room = '';
let pass = '';
let words: string[] = [];
let secretKey: CryptoKey | null = null;

// Sender-only: the plaintext secret, held in memory only until delivered.
let secretText = '';
let secretLabel = '';

// Receiver-only.
let senderPeerId = '';
let revealedSecret = '';

// ── Boot ─────────────────────────────────────────────────
function boot(): void {
  const app = document.getElementById('app')!;
  refs = ui.renderShell(app, {
    how: () => openModal('How Burnwell works', howItWorksHtml()),
    privacy: () => openModal('Privacy & threat model', privacyHtml()),
    about: () => openModal('About Burnwell', aboutHtml()),
    toggleLog: toggleDrawer,
  });
  initModals();
  initGlossary();
  mountDrawer();
  wireGlobalKeys();
  window.addEventListener('pagehide', teardown);

  const invite = readInvite();
  if (invite) {
    role = 'receiver';
    room = invite.room;
    if (invite.pass) {
      startReceiver(room, invite.pass);
    } else {
      ui.renderReceiverPassEntry(refs.view, (p) => startReceiver(room, p));
    }
  } else {
    role = 'sender';
    showCompose();
  }
}

// ── Drawer ───────────────────────────────────────────────
function mountDrawer(): void {
  mountEventDrawer(refs.drawerBody);
  refs.drawerBody.querySelector('#drawer-close')?.addEventListener('click', closeDrawer);
}
function toggleDrawer(): void {
  refs.drawer.classList.contains('open') ? closeDrawer() : openDrawer();
}
function openDrawer(): void {
  refs.drawer.classList.add('open');
  refs.drawer.setAttribute('aria-hidden', 'false');
}
function closeDrawer(): void {
  refs.drawer.classList.remove('open');
  refs.drawer.setAttribute('aria-hidden', 'true');
}
function wireGlobalKeys(): void {
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !isModalOpen() && refs.drawer.classList.contains('open')) closeDrawer();
  });
}

// ── Sender ───────────────────────────────────────────────
function showCompose(): void {
  senderState = 'composing';
  ui.renderSenderCompose(refs.view, { onCreate: createLink });
}

async function createLink(secret: string, label: string, split: boolean): Promise<void> {
  secretText = secret;
  secretLabel = label;
  room = generateRoomId();
  pass = generatePassphrase();
  try {
    secretKey = await deriveSecretKey(pass, room);
    words = await deriveSasWords(pass, room);
  } catch (e) {
    ui.renderError(refs.view, `Could not set up encryption: ${(e as Error).message}`, showCompose);
    return;
  }

  session = new BurnSession(room, pass, {
    onPeerJoin: onSenderPeerJoin,
    onPeerLeave: onSenderPeerLeave,
    onReq: onSenderReq,
    onSecret: () => undefined, // sender never receives a secret frame
    onAck: onSenderAck,
    onGone: () => undefined,
    onError: (m) => ui.renderError(refs.view, m, showCompose),
  });
  if (!session.join()) return;

  senderState = 'armed';
  const link = currentInviteUrl(room, pass, !split);
  emit('info', 'Burn link created — waiting for the recipient');
  ui.renderSenderArmed(
    refs.view,
    { link, words, splitMode: split, passphrase: pass, label, chars: describeSecret(secret).chars, peerCount: 0 },
    {
      onCopyLink: () => copyText(link, 'Link copied'),
      onShare: () => shareLink(link),
      onCopyPass: () => copyText(pass, 'Passphrase copied'),
      onCancel: cancelSend,
    },
  );
}

function onSenderPeerJoin(peerId: string): void {
  const count = session?.getPeerIds().length ?? 0;
  emit('good', `A device connected (${peerId.slice(0, 6)}…)`);
  if (senderState === 'armed') {
    ui.updateSenderStatus(
      refs.view,
      count > 1 ? 'Multiple devices connected — first to reveal wins' : 'Recipient connected — waiting for them to verify & reveal',
      'connected',
      count,
    );
  }
}

function onSenderPeerLeave(peerId: string): void {
  const count = session?.getPeerIds().length ?? 0;
  emit('warn', `A device disconnected (${peerId.slice(0, 6)}…)`);
  if (senderState === 'armed') {
    ui.updateSenderStatus(
      refs.view,
      count > 0 ? 'Waiting for the recipient to verify & reveal' : 'Waiting for the recipient to connect…',
      count > 0 ? 'connected' : 'waiting',
      count,
    );
  }
}

async function onSenderReq(peerId: string): Promise<void> {
  const outcome = senderOnRequest(senderState);
  senderState = outcome.next;
  if (outcome.action === 'gone') {
    session?.sendGone(peerId);
    emit('warn', 'A second device asked for the secret — refused (already delivering)');
    return;
  }
  // Deliver to exactly this one peer.
  try {
    if (!secretKey) throw new Error('no key');
    const plain = encodeSecret({ at: Date.now(), label: secretLabel || undefined }, secretText);
    const { iv, cipher } = await encryptBytes(secretKey, plain);
    ui.updateSenderStatus(refs.view, 'Recipient verified — sending the secret…', 'connected', session?.getPeerIds().length ?? 1);
    await session!.sendSecret(peerId, packFrame(iv, cipher));
    emit('xfer', 'Secret encrypted and sent to the verified recipient');
  } catch (e) {
    senderState = 'armed'; // allow a retry from another reveal
    emit('bad', `Send failed: ${(e as Error).message}`);
    ui.updateSenderStatus(refs.view, 'Send failed — waiting for the recipient to try again', 'error', session?.getPeerIds().length ?? 0);
  }
}

function onSenderAck(): void {
  senderState = senderOnAck(senderState);
  if (senderState !== 'burned') return;
  const when = fmtClock(new Date());
  wipeSecret();
  emit('good', `Delivered & burned at ${when}`);
  teardownSession();
  ui.renderSenderBurned(refs.view, when, resetToCompose);
}

function cancelSend(): void {
  const next = senderOnCancel(senderState);
  if (next !== 'cancelled') return;
  senderState = next;
  wipeSecret();
  emit('warn', 'Burn link cancelled before delivery');
  teardownSession();
  ui.renderSenderCancelled(refs.view, resetToCompose);
}

function resetToCompose(): void {
  room = pass = '';
  words = [];
  secretKey = null;
  showCompose();
}

// ── Receiver ─────────────────────────────────────────────
async function startReceiver(r: string, p: string): Promise<void> {
  room = r;
  pass = normalizePass(p);
  receiverState = 'connecting';
  ui.renderReceiverConnecting(refs.view);
  try {
    secretKey = await deriveSecretKey(pass, room);
    words = await deriveSasWords(pass, room);
  } catch (e) {
    ui.renderError(refs.view, `Could not set up encryption: ${(e as Error).message}`);
    return;
  }
  session = new BurnSession(room, pass, {
    onPeerJoin: onReceiverPeerJoin,
    onPeerLeave: () => undefined,
    onReq: () => undefined,
    onSecret: onReceiverSecret,
    onAck: () => undefined,
    onGone: onReceiverGone,
    onError: (m) => ui.renderError(refs.view, m),
  });
  if (!session.join()) return;
  emit('info', 'Connecting to the sender…');
}

function onReceiverPeerJoin(peerId: string): void {
  if (!senderPeerId) senderPeerId = peerId;
  const next = receiverOnPeer(receiverState);
  if (next === receiverState) return;
  receiverState = next;
  emit('good', 'Connected to the sender — verify the four words');
  ui.renderReceiverReady(refs.view, words, { onReveal });
}

function onReveal(): void {
  const outcome = receiverOnReveal(receiverState);
  if (!outcome.request) return;
  receiverState = outcome.next;
  ui.renderReceiverRequesting(refs.view);
  session?.sendReq(senderPeerId);
  emit('info', 'Reveal requested');
}

async function onReceiverSecret(peerId: string, frame: Uint8Array): Promise<void> {
  const outcome = receiverOnSecret(receiverState);
  if (!outcome.show) return;
  try {
    if (!secretKey) throw new Error('no key');
    const { iv, cipher } = unpackFrame(frame);
    const plain = await decryptBytes(secretKey, iv, cipher);
    const { header, secret } = decodeSecret(plain);
    receiverState = outcome.next;
    revealedSecret = secret;
    session?.sendAck(peerId);
    emit('good', 'Secret received and decrypted — sender has been told to burn');
    ui.renderReceiverRevealed(refs.view, secret, header.label ?? '', {
      onCopy: () => copyText(secret, 'Secret copied'),
      onBurn: burnReceiver,
    });
  } catch (e) {
    emit('bad', `Could not open the secret: ${(e as Error).message}`);
    ui.renderError(refs.view, 'The secret could not be decrypted. The passphrase may be wrong, or the data was corrupted.');
  }
}

function onReceiverGone(): void {
  const next = receiverOnGone(receiverState);
  if (next === receiverState) return;
  receiverState = next;
  emit('warn', 'The sender reports this secret was already delivered');
  teardownSession();
  ui.renderReceiverGone(refs.view);
}

function burnReceiver(): void {
  receiverState = receiverOnBurn(receiverState);
  revealedSecret = '';
  teardownSession();
  emit('good', 'Secret cleared from this screen');
  ui.renderReceiverBurned(refs.view);
}

// ── Shared helpers ───────────────────────────────────────
function wipeSecret(): void {
  secretText = '';
  secretLabel = '';
  secretKey = null;
}

function teardownSession(): void {
  session?.leave();
  session = null;
}

function teardown(): void {
  wipeSecret();
  revealedSecret = '';
  teardownSession();
}

async function copyText(text: string, ok: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast(ok, 'ok');
  } catch {
    toast('Copy failed — select and copy manually', 'err');
  }
}

async function shareLink(link: string): Promise<void> {
  try {
    await navigator.share({ title: 'Burnwell secret', text: 'A one-time encrypted secret is waiting for you:', url: link });
  } catch {
    /* user dismissed */
  }
}

// ── Test hook (headless two-tab verification; drives the REAL pipeline) ──
interface BurnwellTest {
  startSender: (secret: string, label?: string, split?: boolean) => Promise<{ room: string; pass: string; link: string; words: string[] }>;
  startReceiver: (room: string, pass: string) => Promise<void>;
  reveal: () => void;
  getState: () => {
    role: string;
    senderState: SenderState;
    receiverState: ReceiverState;
    revealedSecret: string;
    words: string[];
    peerCount: number;
  };
}
declare global {
  interface Window {
    __burnwellTest?: BurnwellTest;
  }
}
window.__burnwellTest = {
  async startSender(secret, label = '', split = false) {
    role = 'sender';
    await createLink(secret, label, split);
    return { room, pass, link: currentInviteUrl(room, pass, !split), words };
  },
  async startReceiver(r, p) {
    role = 'receiver';
    await startReceiver(r, p);
  },
  reveal() {
    onReveal();
  },
  getState() {
    return {
      role,
      senderState,
      receiverState,
      revealedSecret,
      words,
      peerCount: session?.getPeerIds().length ?? 0,
    };
  },
};

boot();
